import { z } from "zod";
import { esCodigoValido } from "./codigos.js";
import { esquemaContenido } from "./contenido.js";
import { DISPOSICIONES, ETAPAS, MANEJOS, NIVELES_PROTECCION, SITUACIONES } from "./dominio.js";
import { esquemaFechaPapel } from "./fechas.js";
import { esquemaFrecuencia } from "./frecuencia.js";

/**
 * Esquemas Zod compartidos: validan el formulario en la web y la entrada en la API.
 * Los mensajes van en español porque llegan al usuario tal cual. Los de la administración
 * (usuarios, catálogos, puestos) van en `./esquemas-admin.ts`.
 */

export const esquemaLogin = z.object({
  correo: z.email("Escribe un correo válido"),
  contrasena: z.string().min(8, "La contraseña tiene al menos 8 caracteres"),
});
export type Login = z.infer<typeof esquemaLogin>;

export const esquemaCodigoDocumento = z
  .string()
  .trim()
  .toUpperCase()
  .refine(esCodigoValido, "El código tiene la forma PROCESO-TIPO-NNN, ej. LOG-REG-008");

// ---------------------------------------------------------------------------
// Piezas que se repiten en varios formularios
// ---------------------------------------------------------------------------

const nombreDeDocumento = z.string().trim().min(5, "El nombre es muy corto").max(200);

/**
 * El motivo del cambio de una revisión. Era de 500 y no le alcanzaba a Calidad para pegar el
 * historial de cambios entero de un procedimiento (2026-11-03); la base lo guarda sin tope.
 */
export const LARGO_MAXIMO_MOTIVO = 2000;

/** Cuánto se guarda un registro, en meses: de uno a cincuenta años. */
const retencionMeses = z.number().int().min(1).max(600);

const nivelProteccion = z.union([
  z.literal(NIVELES_PROTECCION[0]),
  z.literal(NIVELES_PROTECCION[1]),
  z.literal(NIVELES_PROTECCION[2]),
  z.literal(NIVELES_PROTECCION[3]),
]);

/**
 * Sin el nivel 4, a propósito: un formato cargado desde el papel no entra como confidencial. Ese
 * nivel se asigna en la app, con su lista de personas autorizadas.
 */
const nivelProteccionDeFormato = z.union([
  z.literal(NIVELES_PROTECCION[0]),
  z.literal(NIVELES_PROTECCION[1]),
  z.literal(NIVELES_PROTECCION[2]),
]);

/** SHA-256 en hexadecimal, como lo guarda la base. */
const hashSha256 = /^[0-9a-f]{64}$/;

/**
 * Un archivo que viaja en base64 dentro del JSON de tRPC (multipart cuando haga falta): los
 * tipos que se aceptan y el tamaño máximo en bytes, más los tres mensajes que llegan a la
 * persona. El tope de base64 es el del archivo más el 33 % de la codificación.
 */
function esquemaArchivoBase64<const M extends readonly [string, ...string[]]>(
  mimes: M,
  maxBytes: number,
  mensajes: { mime: string; vacio: string; pesado: string },
) {
  return z.object({
    nombre: z.string().trim().min(1).max(255),
    mime: z.enum(mimes, { error: mensajes.mime }),
    base64: z
      .string()
      .min(1, mensajes.vacio)
      .max(Math.ceil((maxBytes * 4) / 3) + 4, mensajes.pesado),
  });
}

/**
 * Card "Control de registros" de la pantalla 02. Solo aplica si el tipo es registro, y nada de
 * esto bloquea guardar el borrador (acordado el 2026-09-11):
 *
 *  - La copia digital es GestionDOC mismo: no se pregunta dónde está.
 *  - `ubicacion` es solo la de la copia física, y solo si el manejo la tiene. Se exige al enviar
 *    a revisión (`faltaUbicacionFisica`), no al guardar.
 *  - Indexación y responsable, si vienen vacíos, los completa la API (`indexacionPorDefecto` y
 *    el puesto de quien crea).
 */
const textoOpcional = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v ? v : undefined));

/** Sin valores por defecto: al editar, lo que no viene se conserva. */
const esquemaControlRegistrosParcial = z.object({
  ubicacion: textoOpcional(500),
  indexacion: textoOpcional(200),
  responsableAlmacenamiento: textoOpcional(200),
  retencionMeses: retencionMeses.optional(),
  disposicion: z.enum(DISPOSICIONES).optional(),
});

/** Al crear: retención de 2 años y destrucción si no se eligió otra cosa. */
export const esquemaControlRegistros = esquemaControlRegistrosParcial.extend({
  retencionMeses: retencionMeses.default(24),
  disposicion: z.enum(DISPOSICIONES).default("destruccion"),
});
export type ControlRegistros = z.infer<typeof esquemaControlRegistros>;

/** Card "Información general" de la pantalla 02. */
const esquemaNuevoDocumento = z.object({
  procesoCodigo: z.string().trim().toUpperCase().min(2).max(4),
  tipoCodigo: z.string().trim().toUpperCase().min(2).max(4),
  nombre: nombreDeDocumento,
  manejo: z.enum(MANEJOS),
  justificacion: z.string().trim().min(10, "Explica para qué existe el documento").max(2000),
  impacto: z.array(esquemaCodigoDocumento).max(50).default([]),
  nivelProteccion,
  registro: esquemaControlRegistros.optional(),
  /**
   * Fase 3b: si al publicar se le pide acuse de lectura al personal del proceso. Solo cuenta en
   * los documentos informativos (los controlados siempre piden, salvo los registros).
   */
  pideAcuse: z.boolean().default(true),
  /**
   * 2026-10-20: en un tipo "para toda la empresa", a quién va el comunicado. Vacío = a todo el
   * personal activo; con ids = solo a esas personas (más el personal del proceso). Se ignora en
   * los demás tipos.
   */
  destinatarios: z.array(z.string().min(1)).max(500).default([]),
});
export type NuevoDocumento = z.infer<typeof esquemaNuevoDocumento>;

/** Cambiar a quién va un comunicado ya creado (2026-10-20). */
export const esquemaCambiarDestinatarios = z.object({
  documentoId: z.string().min(1),
  destinatarios: z.array(z.string().min(1)).max(500),
});

// ---------------------------------------------------------------------------
// Fase 1 · archivo adjunto, ruta, firma y devolución
// ---------------------------------------------------------------------------

/** Lo que se puede subir como contenido de una revisión (card "Archivo" de la pantalla 02). */
export const MIMES_ARCHIVO = {
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-excel": "xls",
} as const;
export type MimeArchivo = keyof typeof MIMES_ARCHIVO;
export const EXTENSIONES_ARCHIVO = Object.values(MIMES_ARCHIVO);

/** 25 MB. Un procedimiento con fotos pesa 5; un límite mayor invita a subir escaneos sin comprimir. */
export const TAMANO_MAXIMO_ARCHIVO = 25 * 1024 * 1024;

const esquemaArchivoSubido = esquemaArchivoBase64(
  Object.keys(MIMES_ARCHIVO) as [MimeArchivo, ...MimeArchivo[]],
  TAMANO_MAXIMO_ARCHIVO,
  {
    mime: "Solo se aceptan Word, Excel o PDF",
    vacio: "El archivo está vacío",
    pesado: "El archivo supera los 25 MB",
  },
);
export type ArchivoSubido = z.infer<typeof esquemaArchivoSubido>;

/**
 * Card "Ruta de aprobación": se eligen revisor y aprobador. Quien elabora es quien crea, salvo
 * que se indique otra persona en `elaboro` (2026-10-22: Calidad sube los documentos de otros y
 * los revisa; quien elaboró firma esa etapa). En un tipo sin revisor (ruta corta, 2026-10-29) no
 * viene `reviso`: la API lo exige o lo descarta según el tipo.
 */
const esquemaRutaAprobacion = z.object({
  reviso: z.string().min(1, "Elige quién revisa").optional(),
  aprobo: z.string().min(1, "Elige quién aprueba"),
});

/**
 * Pantalla 02 completa: información general + control de registros + archivo + ruta.
 *
 * Fase 3: sin `archivo`, la revisión se **redacta en el editor** y nace con las secciones de la
 * plantilla de su tipo, vacías. Subir un archivo sigue siendo válido para cualquier tipo; los
 * tipos sin plantilla (externos, matrices, planos…) solo admiten archivo.
 */
export const esquemaCrearDocumento = esquemaNuevoDocumento.extend({
  motivo: z.string().trim().max(LARGO_MAXIMO_MOTIVO).default("Versión inicial"),
  /** Obligatoria salvo en los tipos sin ruta de aprobación (informativos, fase 3b). */
  ruta: esquemaRutaAprobacion.optional(),
  /** Quien elaboró, si no es quien lo sube: debe poder cargar en el proceso. Ausente = quien crea. */
  elaboro: z.string().min(1).optional(),
  archivo: esquemaArchivoSubido.optional(),
  /** Entra vigente de una vez: exige archivo y el permiso temporal de la persona. */
  sinFirmas: z.boolean().optional(),
});

/**
 * Modal 03b: la firma es la imagen que Soporte Tecnológico cargó para el usuario, más su
 * contraseña. La etapa la decide el estado de la revisión, no el cliente.
 */
export const esquemaFirma = z.object({
  revisionId: z.uuid(),
  /**
   * El hash del contenido que la persona tiene en pantalla. Si la revisión cambió desde que la
   * abrió (se devolvió y se subió otro archivo), la firma se rechaza: nadie firma lo que no vio.
   */
  hashContenido: z.string().regex(hashSha256, "Falta el contenido que se firma"),
  contrasena: z.string().min(1, "Confirma con tu contraseña").max(200),
  /**
   * La etapa que la persona tiene en pantalla. Si la revisión ya avanzó (un doble clic, otra
   * pestaña), no se firma la etapa siguiente por error: importa cuando la misma persona revisa
   * y aprueba.
   */
  etapa: z.enum(ETAPAS).optional(),
});
export type Firma = z.infer<typeof esquemaFirma>;

/** Imagen de firma de una persona: PNG o JPEG (se estampa en el PDF), 2 MB como mucho. */
const MIMES_IMAGEN_FIRMA = ["image/png", "image/jpeg"] as const;
export const TAMANO_MAXIMO_IMAGEN_FIRMA = 2 * 1024 * 1024;

export const esquemaImagenFirma = z.object({
  usuarioId: z.string().min(1),
  archivo: esquemaArchivoBase64(MIMES_IMAGEN_FIRMA, TAMANO_MAXIMO_IMAGEN_FIRMA, {
    mime: "La firma tiene que ser PNG o JPEG",
    vacio: "La imagen está vacía",
    pesado: "La imagen supera los 2 MB",
  }),
});

export const esquemaDevolucion = z.object({
  revisionId: z.uuid(),
  /**
   * Fase 4: si la revisión tiene marcas abiertas en el texto, las observaciones son opcionales
   * (las marcas ya dicen qué corregir). Sin marcas, la API las exige.
   */
  observaciones: z
    .string()
    .trim()
    .max(4000, "Las observaciones son demasiado largas")
    .refine((v) => v.length === 0 || v.length >= 5, "Explica qué hay que corregir")
    .default(""),
});

export const esquemaReemplazarArchivo = z.object({
  revisionId: z.uuid(),
  archivo: esquemaArchivoSubido,
});

/** Filtros de la lista maestra (pantalla 01). */
export const esquemaFiltroLista = z.object({
  procesoCodigo: z.string().trim().toUpperCase().min(2).max(4),
  busqueda: z.string().trim().max(100).optional(),
  tipoCodigo: z.string().trim().toUpperCase().max(4).optional(),
  situacion: z.enum(SITUACIONES).optional(),
});

/**
 * "Abrir nueva revisión" (pantalla 03): la Rev. N+1 de un documento vigente. Nace en borrador
 * con su archivo y su motivo; la ruta parte de la de la vigente y se puede cambiar.
 */
export const esquemaAbrirRevision = z.object({
  documentoId: z.uuid(),
  motivo: z
    .string()
    .trim()
    .min(5, "Explica qué cambia en esta revisión")
    .max(LARGO_MAXIMO_MOTIVO, `El motivo pasa de ${LARGO_MAXIMO_MOTIVO} caracteres`),
  /**
   * Sin archivo, la revisión nueva se redacta: arranca con todo el contenido de la vigente, ya
   * sea redactada o un archivo (se copia al editor).
   */
  archivo: esquemaArchivoSubido.optional(),
  /** Cuando el archivo de la vigente no se puede copiar: redactar la revisión desde cero. */
  enBlanco: z.boolean().optional(),
  ruta: esquemaRutaAprobacion.optional(),
  /** Quien elaboró, si no es quien la abre: debe poder cargar en el proceso. Ausente = quien abre. */
  elaboro: z.string().min(1).optional(),
  /** Entra vigente de una vez: exige archivo y el permiso temporal de la persona. */
  sinFirmas: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Fase 3 · contenido redactado en el editor
// ---------------------------------------------------------------------------

/**
 * Autoguardado de la pantalla 05: el JSON entero cada vez (un documento pesa kilobytes, no vale
 * la pena mandar diffs). `hashConocido` es el hash que el editor tenía al cargar o al guardar por
 * última vez: si la revisión ya no lo tiene (otra pestaña guardó antes), la API no pisa y avisa.
 */
export const esquemaGuardarContenido = z.object({
  revisionId: z.uuid(),
  contenido: esquemaContenido,
  hashConocido: z.string().regex(hashSha256).optional(),
});

/** Imagen para el bloque `imagen` del editor: PNG o JPEG, 5 MB como mucho (va al PDF tal cual). */
export const MIMES_IMAGEN_CONTENIDO = ["image/png", "image/jpeg"] as const;
export const TAMANO_MAXIMO_IMAGEN_CONTENIDO = 5 * 1024 * 1024;

export const esquemaImagenContenido = z.object({
  revisionId: z.uuid(),
  archivo: esquemaArchivoBase64(MIMES_IMAGEN_CONTENIDO, TAMANO_MAXIMO_IMAGEN_CONTENIDO, {
    mime: "La imagen tiene que ser PNG o JPEG",
    vacio: "La imagen está vacía",
    pesado: "La imagen supera los 5 MB",
  }),
});

/** "Cambiar revisor o aprobador" (pantalla 02 y 03). Quien elabora no cambia. */
export const esquemaCambiarRuta = esquemaRutaAprobacion.extend({ revisionId: z.uuid() });

/**
 * Completar o corregir el control de registros (y el manejo) mientras hay un borrador. Es lo
 * que se usa para cargar la ubicación física antes de enviar a revisión.
 */
export const esquemaEditarControl = z.object({
  revisionId: z.uuid(),
  manejo: z.enum(MANEJOS),
  registro: esquemaControlRegistrosParcial.optional(),
});

/** Corregir el nombre de una vigente subida sin firmas (permiso temporal, 2026-11-03). */
export const esquemaCorregirNombre = z.object({
  revisionId: z.uuid(),
  nombre: nombreDeDocumento,
});

/**
 * Fase 3b · "Cargar formato existente": un formato que ya está aprobado en papel (en
 * CSGestión) entra vigente de una vez, con su número de revisión y su fecha de aprobación, sin
 * pasar por las tres firmas de la app. Lo hace un administrador. El archivo (PDF, o Word que se convierte)
 * es la plantilla: las hojas se llenan escribiendo encima de sus páginas.
 */
export const esquemaCargarFormato = z.object({
  codigo: esquemaCodigoDocumento,
  nombre: nombreDeDocumento,
  revision: z.number().int().min(1, "La revisión empieza en 1").max(99),
  /** La fecha de aprobación que dice el papel. */
  aprobadaEl: esquemaFechaPapel(),
  manejo: z.enum(MANEJOS).default("fisico_y_digital"),
  nivelProteccion: nivelProteccionDeFormato.default(2),
  retencionMeses: retencionMeses.default(24),
  archivo: esquemaArchivoSubido,
  /** Quien lo carga lo llena: queda en sus Diligencias. */
  loLlenoYo: z.boolean().default(true),
  /** Cada cuánto le toca (si `loLlenoYo`); si no se dice, todos los días. */
  frecuencia: esquemaFrecuencia.default({ tipo: "diaria" }),
});
export type CargarFormato = z.input<typeof esquemaCargarFormato>;

// ---------------------------------------------------------------------------
// Fase 5 · Importar historial (card "Importar historial" de la pantalla 02)
// ---------------------------------------------------------------------------

/**
 * Los archivos del historial suben uno por uno antes de importar (25 MB cada uno no caben
 * juntos en una sola petición). El código y la revisión dan el nombre con que se guarda.
 */
export const esquemaSubirArchivoHistorial = z.object({
  codigo: esquemaCodigoDocumento,
  revision: z.number().int().min(1).max(99),
  archivo: esquemaArchivoSubido,
});

/** Quién firmó una etapa en papel. Sin nombre = el papel no lo dice (se omite). */
const esquemaFirmaPapel = z.object({
  nombre: z.string().trim().max(120).default(""),
  puesto: z.string().trim().max(120).default(""),
});

const esquemaRevisionHistorica = z.object({
  numero: z.number().int().min(1, "La revisión empieza en 1").max(99),
  /** Lo devuelve `subirArchivoHistorial`. */
  archivoId: z.uuid("Falta el archivo de esta revisión"),
  aprobadaEl: esquemaFechaPapel(),
  motivo: z.string().trim().min(3, "Escribe el motivo del cambio").max(LARGO_MAXIMO_MOTIVO),
  firmas: z
    .object({
      elaboro: esquemaFirmaPapel.optional(),
      reviso: esquemaFirmaPapel.optional(),
      aprobo: esquemaFirmaPapel.optional(),
    })
    .default({}),
});

/**
 * Un documento que ya existe en papel (CSGestión) entra con todas sus revisiones de una vez:
 * cada una con su archivo, su fecha de aprobación, su motivo y quién la firmó en papel. Todas
 * quedan obsoletas salvo la última, que queda vigente. Lo hace un administrador.
 */
export const esquemaImportarHistorial = z
  .object({
    codigo: esquemaCodigoDocumento,
    nombre: nombreDeDocumento,
    manejo: z.enum(MANEJOS).default("digital"),
    justificacion: z.string().trim().max(2000).default(""),
    nivelProteccion: nivelProteccion.default(2),
    /** Solo para registros: cuánto se guarda cada hoja (como en "Cargar formato"). */
    retencionMeses: retencionMeses.default(24),
    revisiones: z
      .array(esquemaRevisionHistorica)
      .min(1, "Agrega al menos una revisión")
      .max(50, "Son demasiadas revisiones para una importación"),
  })
  .superRefine((d, ctx) => {
    d.revisiones.forEach((r, i) => {
      const previa = d.revisiones[i - 1];
      if (!previa) return;
      if (r.numero <= previa.numero) {
        ctx.addIssue({
          code: "custom",
          path: ["revisiones", i, "numero"],
          message: "Las revisiones van en orden: cada una con un número mayor que la anterior",
        });
      }
      if (r.aprobadaEl < previa.aprobadaEl) {
        ctx.addIssue({
          code: "custom",
          path: ["revisiones", i, "aprobadaEl"],
          message: "No puede ser anterior a la aprobación de la revisión previa",
        });
      }
    });
  });
export type ImportarHistorial = z.input<typeof esquemaImportarHistorial>;

// --- Retirar un documento (administrador) ---------------------------------------------------

export const esquemaRetiroDocumento = z.object({
  codigo: esquemaCodigoDocumento,
  motivo: z
    .string()
    .trim()
    .min(10, "Explica por qué se retira: queda en la auditoría y lo ve el dueño del proceso")
    .max(1000, "El motivo es demasiado largo"),
});
